import { useMemo, useState } from "react";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import PhysicalStoreService from "../../../../service/PhysicalStoreService";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import { formatDay } from "../../utils/adminUtils";
import StatusTabs from "../../components/list/StatusTabs";
import CatalogTable from "../../components/list/CatalogTable";
import CatalogThumb from "../../components/list/CatalogThumb";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";
import FormDialog from "../../components/form/FormDialog";
import FormField from "../../components/form/FormField";
import StoreDetailsDialog from "./StoreDetailsDialog";
import { toApprovedRow, toPendingRow } from "./physicalStoreUtils";
import "./AdminPhysicalStores.css";

const TABS = { PENDING: "pending", APPROVED: "approved" };

const loadPending = async () =>
  (expectCode(await new PhysicalStoreService().getPendingVerifications(), 200001).result || []).map(
    toPendingRow,
  );

const loadApproved = async () =>
  (expectCode(await new PhysicalStoreService().getApprovedStores(), 200001).result || []).map(
    toApprovedRow,
  );

const AdminPhysicalStores = () => {
  const pending = useAdminList(loadPending);
  const approved = useAdminList(loadApproved);
  const { run, runningId } = useAdminAction();
  const [tab, setTab] = useState(TABS.PENDING);
  const [details, setDetails] = useState(null);
  const [toApprove, setToApprove] = useState(null);
  const [toReject, setToReject] = useState(null);

  const current = tab === TABS.PENDING ? pending : approved;
  const isPendingTab = tab === TABS.PENDING;

  const counts = useMemo(
    () => ({ [TABS.PENDING]: pending.items.length, [TABS.APPROVED]: approved.items.length }),
    [pending.items, approved.items],
  );

  const reloadAll = () => {
    pending.reload();
    approved.reload();
  };

  const approve = async () => {
    const store = toApprove;
    const ok = await run(
      store.id,
      async () => expectCode(await new PhysicalStoreService().approveStore(store.owner), 200000),
      "storeApprovedSuccessfully",
    );
    setToApprove(null);
    if (ok) reloadAll();
  };

  const reject = async () => {
    if (!toReject.reason.trim()) {
      setToReject((prev) => ({ ...prev, error: localeText("enterRejectionReason") }));
      return;
    }
    const { store, reason } = toReject;
    const ok = await run(
      store.id,
      async () =>
        expectCode(await new PhysicalStoreService().rejectStore(store.owner, reason.trim()), 200000),
      "storeRejectedSuccessfully",
    );
    if (ok) {
      setToReject(null);
      reloadAll();
    }
  };

  const openApprove = (store) => {
    setDetails(null);
    setToApprove(store);
  };

  const openReject = (store) => {
    setDetails(null);
    setToReject({ store, reason: "", error: null });
  };

  return (
    <div className="admin-list">
      <StoreDetailsDialog
        store={details}
        onClose={() => setDetails(null)}
        onApprove={openApprove}
        onReject={openReject}
      />

      <ConfirmActionDialog
        visible={!!toApprove}
        title={localeText("approveStore")}
        message={localeText("confirmApproveStore")}
        details={
          toApprove
            ? [
                { label: localeText("physicalStoreName"), value: toApprove.name },
                { label: localeText("storeOwner"), value: toApprove.owner && `@${toApprove.owner}` },
                { label: localeText("adminBackofficeAddress"), value: toApprove.address },
              ]
            : []
        }
        confirmLabel={localeText("approveStore")}
        loading={!!toApprove && runningId === toApprove.id}
        onConfirm={approve}
        onCancel={() => setToApprove(null)}
      />

      <FormDialog
        visible={!!toReject}
        title={localeText("rejectStore")}
        subtitle={toReject?.store?.name}
        saveLabel={localeText("rejectStore")}
        saving={!!toReject && runningId === toReject.store.id}
        onSave={reject}
        onCancel={() => setToReject(null)}
        width="30rem"
      >
        {toReject && (
          <FormField
            label={localeText("enterRejectionReason")}
            htmlFor="store-reject-reason"
            error={toReject.error}
            hint={localeText("adminStoresRejectHint")}
            required
          >
            <textarea
              id="store-reject-reason"
              className="admin-input admin-textarea"
              rows={4}
              value={toReject.reason}
              onChange={(e) => setToReject((prev) => ({ ...prev, reason: e.target.value, error: null }))}
              autoFocus
            />
          </FormField>
        )}
      </FormDialog>

      <StatusTabs
        tabs={[
          { value: TABS.PENDING, label: localeText("physicalStoresPending") },
          { value: TABS.APPROVED, label: localeText("physicalStoresApproved") },
        ]}
        value={tab}
        onChange={setTab}
        counts={counts}
        loading={pending.loading || approved.loading}
      />

      <CatalogTable
        key={tab}
        rows={current.items}
        loading={current.loading}
        loadError={current.loadError}
        onReload={current.reload}
        searchPlaceholder={localeText("adminStoresSearch")}
        sortField="submittedValue"
        sortOrder={isPendingTab ? 1 : -1}
        minWidth="56rem"
        emptyIcon={isPendingTab ? "pi pi-check-circle" : "pi pi-building"}
      >
        <Column
          header={localeText("physicalStoreName")}
          field="name"
          body={(store) => (
            <div className="admin-catalog__item">
              <CatalogThumb imageId={store.photoId} icon="pi pi-building" />
              <div className="admin-stack">
                <span className="admin-strong">{store.name || "—"}</span>
                {store.city && <span className="admin-muted">{store.city}</span>}
              </div>
            </div>
          )}
          sortable
        />
        <Column
          header={localeText("adminBackofficeAddress")}
          field="address"
          body={(store) => <span className="admin-store__address">{store.address || "—"}</span>}
        />
        <Column
          header={localeText("storeOwner")}
          field="owner"
          body={(store) => (store.owner ? `@${store.owner}` : "—")}
          sortable
        />
        <Column
          header={localeText("submittedAt")}
          field="submittedValue"
          body={(store) => formatDay(store.submittedAt)}
          sortable
        />
        <Column
          header={localeText("actions")}
          frozen
          alignFrozen="right"
          headerClassName="admin-list__actions-cell"
          bodyClassName="admin-list__actions-cell"
          body={(store) => (
            <div className="admin-list__header-actions">
              <button type="button" className="admin-btn" onClick={() => setDetails(store)}>
                <i className="pi pi-eye" aria-hidden="true" />
                {localeText("viewDetails")}
              </button>
              {isPendingTab && (
                <>
                  <button
                    type="button"
                    className="admin-icon-btn admin-btn--danger-hover"
                    onClick={() => openReject(store)}
                    disabled={runningId === store.id}
                    title={localeText("rejectStore")}
                    aria-label={localeText("rejectStore")}
                  >
                    <i className="pi pi-times" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn--primary"
                    onClick={() => openApprove(store)}
                    disabled={runningId === store.id}
                  >
                    <i
                      className={runningId === store.id ? "pi pi-spin pi-spinner" : "pi pi-check"}
                      aria-hidden="true"
                    />
                    {localeText("adminStoresApproveShort")}
                  </button>
                </>
              )}
            </div>
          )}
        />
      </CatalogTable>
    </div>
  );
};

export default AdminPhysicalStores;
